'use client';

import { useState, useEffect } from 'react';
import { ChevronDown, Globe } from 'lucide-react';
import { languageOptions, LanguageOption } from '@/config/i18n';
import { useLanguage } from './LanguageProvider';
import { motion } from 'framer-motion';
import { logger } from '@/utils/logger';

export function LanguageSelector() {
  const [isOpen, setIsOpen] = useState(false);
  const [shouldPulse, setShouldPulse] = useState(false);
  const { currentLang, setCurrentLang } = useLanguage();
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageOption | null>(null);

  // Update selected language whenever currentLang changes
  useEffect(() => {
    const newSelectedLanguage = languageOptions.find((lang) => lang.code === currentLang);
    if (newSelectedLanguage) {
      setSelectedLanguage(newSelectedLanguage);
    } else {
      // Fallback to first language if current language not found in options
      setSelectedLanguage(languageOptions[0]);
      logger.warn(`Language ${currentLang} not found in language options`);
    }
  }, [currentLang]);

  // Pulse the language selector for Chinese browser users on first visit
  useEffect(() => {
    const browserLanguages = navigator.languages || [navigator.language];
    const isChinesePreferred = browserLanguages.some((lang) =>
      lang.toLowerCase().match(/^zh|^zh-cn$|^zh-tw$|^zh-hk$/)
    );

    if (isChinesePreferred) {
      const hasSeenPulse = localStorage.getItem('hasSeenLanguagePulse');
      if (!hasSeenPulse) {
        setShouldPulse(true);
        localStorage.setItem('hasSeenLanguagePulse', 'true');
        setTimeout(() => setShouldPulse(false), 6000);
      }
    }
  }, []);

  const handleLanguageChange = (option: LanguageOption) => {
    // Use the setCurrentLang from context - this will handle URL updates
    setCurrentLang(option.code);
    setIsOpen(false);
  };

  // Don't render until we have a selected language
  if (!selectedLanguage) return null;

  return (
    <div className="relative text-sm">
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center text-xs text-white focus:outline-none"
        aria-label="Select language"
        aria-expanded={isOpen}
        aria-haspopup="true"
        animate={
          shouldPulse
            ? {
                color: ['rgb(209 213 219)', 'rgb(255 255 255)', 'rgb(209 213 219)'],
              }
            : {}
        }
        transition={
          shouldPulse
            ? {
                duration: 0.6,
                repeat: 9, // 10 total pulses (zero-based)
                ease: 'easeInOut',
              }
            : {}
        }
      >
        <Globe className="mr-2 h-4 w-4 opacity-50" aria-hidden="true" />
        <span>{selectedLanguage.name}</span>
        <ChevronDown className="ml-1 h-4 w-4" />
      </motion.button>
      {isOpen && (
        <div className="absolute -right-1 z-50 mt-6 w-48 rounded-md bg-[#111] shadow-lg ring-1 ring-black ring-opacity-5">
          <div
            className="pb-2 pt-1"
            role="menu"
            aria-orientation="vertical"
            aria-labelledby="options-menu"
          >
            {languageOptions.map((option) => (
              <button
                aria-label={`Switch to ${option.name}`}
                key={option.code}
                data-language={option.name}
                onClick={() => handleLanguageChange(option)}
                className={`language-switch block w-full px-4 py-2 text-left text-xs ${
                  option.code === currentLang
                    ? 'bg-gray-800 text-white'
                    : 'text-gray-300 hover:text-gray-200'
                }`}
                role="menuitem"
              >
                <span className="relative top-1 mr-2 text-lg">{option.flag}</span>
                {option.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
